import { Callout, Intent } from '@blueprintjs/core'
import React, { FC, useEffect } from 'react'
import { formatMoney } from '../../../../../utils'
import { UprightStyle } from '../../../../../types'
import { PriceRecommendationDialog } from './PriceRecommendationDialog'
import { PriceRecommendations } from './types'
import { getMinimumThresholdPrices } from './Helpers'

export interface PriceRecommendationsProps {
  recommendations: {
    phase_2: boolean // this is as a minimum relevence threshold test, it comes from the API and must be true to show price recommendation results
    org: PriceRecommendations[] // these are the org-specific recommendations
    global: PriceRecommendations[] // these are the aggregated recommendations that have been obfuscated
    queryMetadataTitle: string
  }
  onChange: any
  startingPrice?: boolean // whether or not we display Starting Price data
  binPrice?: boolean // whether or not we display BIN Price data
  listingType?: string // this is used for analytics tracking
}

export const styles: UprightStyle = {
  putThisOneOnANewGridLine: { gridColumnStart: 1, background: '#EBF1F5' },
  stretchFull: { gridColumn: '1 / span 3', gridColumnStart: 1, background: '#EBF1F5' }
}

export const PriceRecommendation: FC<PriceRecommendationsProps> = props => {
  const { recommendations, onChange, startingPrice, binPrice, listingType } = props

  const [open, setOpen] = React.useState(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedOnListingChange = React.useCallback(onChange, [])

  // For analytics tracking
  useEffect(() => {
    memoizedOnListingChange({ name: 'buyingFormat', value: { startingPrice, binPrice, listingType } })
  }, [memoizedOnListingChange, startingPrice, binPrice, listingType])

  const getPriceRange = (prices: number[]) => {
    if (prices.length === 0) return [null, null]
    const sortedPrices = prices.sort((a, b) => a - b)
    return [sortedPrices[0], sortedPrices[sortedPrices.length - 1]]
  }

  const [bottomPrice, upperPrice] = getPriceRange(getMinimumThresholdPrices(recommendations?.org || [], 10))

  let bottomStartingPrice
  let upperStartingPrice
  let bottomBINPrice
  let upperBINPrice

  if (startingPrice)
    [bottomStartingPrice, upperStartingPrice] = getPriceRange(
      getMinimumThresholdPrices(recommendations?.org || [], 10, startingPrice)
    )

  if (binPrice)
    [bottomBINPrice, upperBINPrice] = getPriceRange(
      getMinimumThresholdPrices(recommendations?.org || [], 10, startingPrice, binPrice)
    )

  let soldForPriceDisplay = 'N/A'
  if (recommendations?.org?.length > 1)
    if (bottomPrice === upperPrice) soldForPriceDisplay = formatMoney(bottomPrice)
    else soldForPriceDisplay = `${formatMoney(bottomPrice)} - ${formatMoney(upperPrice)}`
  else if (recommendations?.org?.length === 1 && recommendations.org[0].price)
    soldForPriceDisplay = formatMoney(parseFloat(recommendations.org[0].price))

  let startingPriceDisplay = 'N/A'
  if (bottomStartingPrice > 0)
    if (recommendations?.org?.length > 1)
      if (bottomStartingPrice === upperStartingPrice) startingPriceDisplay = formatMoney(bottomStartingPrice)
      else startingPriceDisplay = `${formatMoney(bottomStartingPrice)} - ${formatMoney(upperStartingPrice)}`
    else startingPriceDisplay = formatMoney(parseFloat(recommendations.org[0].startingPrice))

  let BINPriceDisplay = 'N/A'
  if (bottomBINPrice > 0)
    if (recommendations?.org?.length > 1)
      if (bottomBINPrice === upperBINPrice) BINPriceDisplay = formatMoney(bottomBINPrice)
      else BINPriceDisplay = `${formatMoney(bottomBINPrice)} - ${formatMoney(upperBINPrice)}`
    else BINPriceDisplay = formatMoney(parseFloat(recommendations.org[0].buyNowPrice))

  return (
    <>
      {recommendations?.phase_2 && recommendations?.org?.length > 0 && (
        <>
          <Callout
            id="price-prediction-section"
            style={styles.stretchFull}
            className="stretch-full"
            intent={Intent.PRIMARY}
            icon="series-search"
          >
            <span style={{ color: '#5C7080' }} className="bp5-heading">
              Price Predictions
            </span>
            <span style={styles.putThisOneOnANewGridLine} className="stretch-full">
              <a
                style={{ float: 'right' }}
                onClick={() => {
                  setOpen(true)
                  onChange({ name: 'clickedViewHistoricalListings', value: true })
                }}
              >
                View historical listings
              </a>
            </span>
            <span style={styles.putThisOneOnANewGridLine} className="stretch-full">
              &nbsp;&nbsp;&nbsp;&nbsp;
              {bottomPrice === upperPrice ? 'Similar item sold for:' : 'Similar items sold for:'} &nbsp;
              <strong>{soldForPriceDisplay}</strong>
              {startingPrice && bottomStartingPrice > 0 && (
                <>
                  &nbsp;|&nbsp; Starting {bottomStartingPrice === upperStartingPrice ? 'Price' : 'Prices'}: &nbsp;{' '}
                  <strong>{startingPriceDisplay}</strong>
                </>
              )}
              {binPrice && bottomBINPrice > 0 && (
                <>
                  &nbsp;|&nbsp; BIN {bottomBINPrice === upperBINPrice ? 'Price' : 'Prices'}: &nbsp;{' '}
                  <strong>{BINPriceDisplay}</strong>
                </>
              )}
            </span>
          </Callout>
          <PriceRecommendationDialog
            showPricePredictionsDialog={open}
            showBINPrice={binPrice}
            showStartingPrice={startingPrice}
            onClose={averages => {
              onChange(averages)
              setOpen(false)
            }}
            orgPredictions={recommendations.org}
            globalPredictions={recommendations.global}
            query={recommendations.queryMetadataTitle}
          />
        </>
      )}
    </>
  )
}
